import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, FileCheck, ArrowRight, Sparkles, Play, Star } from "lucide-react";

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-24 pb-24 sm:pt-32 sm:pb-32 overflow-hidden bg-stone-50">
      {/* Layered backdrop: grid + orbs + noise for a premium feel */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(6,95,70,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(6,95,70,0.08) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(ellipse at top, black 35%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, black 35%, transparent 75%)",
        }}
      />
      {/* Decorative orbs — static on mobile (animating a blur(64px) 520px element OOMs iOS Safari / low-end Android). */}
      <div className="absolute -top-24 right-1/4 w-[420px] h-[420px] sm:w-[520px] sm:h-[520px] rounded-full bg-emerald-200/40 blur-2xl sm:blur-3xl pointer-events-none will-change-transform hidden sm:block lg:animate-float-slow" />
      <div className="absolute -top-20 right-10 w-[260px] h-[260px] rounded-full bg-emerald-200/50 blur-2xl pointer-events-none sm:hidden" />
      <div className="absolute top-60 -left-24 w-[260px] h-[260px] sm:w-[360px] sm:h-[360px] rounded-full bg-orange-200/50 blur-2xl sm:blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* LEFT — Headline block */}
          <div className="lg:col-span-7 animate-fade-up">
            {/* Live rating pill */}
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur border border-stone-200 shadow-sm text-xs font-semibold text-stone-700"
            >
              <span className="flex items-center -space-x-1">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                ))}
              </span>
              <span className="text-stone-500">·</span>
              <span>Built for Ohio-licensed centers</span>
              <span className="ml-1 inline-flex items-center gap-1 text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" /> live
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="mt-6 font-display text-[clamp(2.75rem,6vw,5.5rem)] font-semibold text-stone-900 tracking-[-0.03em] leading-[0.98]"
            >
              The calm ops system for{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-600 bg-clip-text text-transparent">
                  Ohio childcare
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" height="14" viewBox="0 0 360 14" fill="none">
                  <path d="M2 9 C 60 2, 120 14, 180 7 S 300 2, 358 10" stroke="#fb923c" strokeWidth="4" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              .
            </h1>

            <p data-testid="hero-subtitle" className="mt-7 text-lg sm:text-xl text-stone-600 leading-relaxed max-w-2xl">
              One calm workspace to run a licensed Ohio center — enrollment, attendance,
              billing, lesson plans, and every DCY form in one place.
            </p>
            <p data-testid="hero-disclaimer" className="mt-3 text-xs text-stone-500 max-w-2xl">
              Independent software for childcare operators. <strong className="text-stone-700">Not affiliated with, endorsed by, or funded by</strong> the State of Ohio or the Ohio Department of Children &amp; Youth.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button
                asChild
                data-testid="hero-start-trial-btn"
                className="group bg-stone-900 hover:bg-stone-800 text-white rounded-full font-medium px-7 py-6 text-base shadow-[0_12px_30px_-10px_rgba(6,95,70,0.45)] hover:shadow-[0_16px_40px_-10px_rgba(6,95,70,0.6)] hover:-translate-y-0.5 transition-all"
              >
                <a href="#pricing">
                  <Sparkles className="w-4 h-4 mr-2 text-emerald-300 group-hover:rotate-12 transition-transform" />
                  Start 14-day free trial
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                variant="ghost"
                data-testid="hero-book-demo-btn"
                className="group rounded-full font-medium px-5 py-6 text-base text-stone-700 hover:text-stone-900 hover:bg-white"
              >
                <a href="#demo" className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-white border border-stone-200 grid place-items-center group-hover:border-emerald-700 group-hover:text-emerald-700 transition-colors">
                    <Play className="w-3 h-3 ml-0.5 fill-current" />
                  </span>
                  Watch 2-min tour
                </a>
              </Button>
            </div>

            {/* Trust row */}
            <div className="mt-12 pt-6 border-t border-stone-200 flex flex-wrap items-center gap-x-8 gap-y-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-stone-500">Built to pass audits</div>
              {[
                { Icon: FileCheck,   label: "Every DCY form" },
                { Icon: ShieldCheck, label: "SOC 2 ready" },
                { Icon: Lock,        label: "HIPAA-grade" },
              ].map(({ Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 text-sm text-stone-700"
                  data-testid={`trust-badge-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Icon className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span className="font-semibold">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Product preview with floating cards */}
          <div className="lg:col-span-5 relative">
            <div className="relative">
              {/* Accent backplate */}
              <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100 via-white to-orange-100 rounded-[2.5rem] blur-2xl opacity-60" />

              {/* Dashboard mock */}
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-25px_rgba(6,95,70,0.35)] border border-stone-200 bg-white">
                <div className="h-8 bg-stone-50 border-b border-stone-200 flex items-center px-3 gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  <span className="ml-3 text-[10px] text-stone-500 font-medium">childcheckoh.com/dashboard</span>
                </div>
                <div className="aspect-[4/5] relative">
                  <img
                    src="https://images.pexels.com/photos/8613089/pexels-photo-8613089.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=750"
                    alt="ChildCheck dashboard shown on a tablet in a bright Ohio classroom"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/40 via-transparent to-transparent" />
                </div>
              </div>

              {/* Floating ratio card */}
              <div
                data-testid="hero-floating-ratio"
                className="absolute -left-6 top-16 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 w-60 lg:animate-float-slow"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">
                    Live ratio · Toddler A
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    OK
                  </span>
                </div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="font-display text-4xl font-semibold text-stone-900">1:7</div>
                  <div className="text-xs text-stone-500 pb-1.5">OAC 5101:2-12-18</div>
                </div>
                <div className="mt-3 flex items-center gap-1">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-6 w-1.5 rounded-full ${i < 7 ? "bg-emerald-500" : "bg-stone-200"}`}
                      style={{ opacity: i < 7 ? 1 - i * 0.05 : 1 }}
                    />
                  ))}
                </div>
              </div>

              {/* Floating parent-check-in card */}
              <div
                data-testid="hero-floating-attendance"
                className="absolute -right-4 bottom-16 bg-white rounded-2xl shadow-xl border border-stone-200 p-4 w-64 lg:animate-float-slow"
                style={{ animationDelay: "1.5s" }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 grid place-items-center text-white">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700">Today</div>
                    <div className="text-xs text-stone-500">Check-ins · Columbus</div>
                  </div>
                </div>
                <div className="flex items-end justify-between">
                  <div className="font-display text-3xl font-semibold text-stone-900">47<span className="text-stone-400 text-xl">/52</span></div>
                  <div className="flex -space-x-2">
                    {["#fde68a", "#bbf7d0", "#fed7aa", "#a7f3d0"].map((c, i) => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-white shadow-sm" style={{ background: c }} />
                    ))}
                    <div className="w-7 h-7 rounded-full border-2 border-white bg-stone-900 text-white grid place-items-center text-[10px] font-semibold">
                      +43
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating DCY 01234 card */}
              <div
                data-testid="hero-floating-form"
                className="absolute -right-3 top-0 bg-emerald-950 text-white rounded-2xl shadow-xl p-3.5 w-48 lg:animate-float-slow"
                style={{ animationDelay: "0.8s" }}
              >
                <div className="flex items-center gap-2">
                  <i className="fa-regular fa-file-lines text-emerald-300" />
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-300">DCY 01234</div>
                </div>
                <div className="font-display text-sm mt-2 leading-tight">e-signed by parent</div>
                <div className="text-[10px] text-emerald-200/70 mt-1 flex items-center gap-1">
                  <span className="w-1 h-1 rounded-full bg-emerald-300 animate-pulse" /> 12s ago
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ohio framework ticker — the public references we actually build against. */}
        <div className="mt-20 pt-10 border-t border-stone-200">
          <div className="text-center text-[10px] uppercase tracking-[0.25em] font-bold text-stone-500 mb-6">
            Purpose-built around Ohio rules — Columbus · Cleveland · Cincinnati · Toledo · Akron · Dayton · Youngstown
          </div>
          <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4 opacity-80">
            {[
              "DCY",
              "Ohio Dept. of Children & Youth",
              "OAC 5101:2-12",
              "Ohio ELDS",
              "DCY 01281 Playground",
              "DCY 01234 Enrollment",
            ].map((name) => (
              <span key={name} className="font-display text-base sm:text-lg text-stone-700 font-medium tracking-tight">
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
